import React, { useEffect, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import PhoneMockup from "./PhoneMockup";
import DashboardScreen from "./AppScreens/DashboardScreen";
import OffersScreen from "./AppScreens/OffersScreen";
import DocumentsScreen from "./AppScreens/DocumentsScreen";
import { cn } from "@/lib/utils";

const SCREENS = [
  { key: "dashboard", label: "Dashboard", Component: DashboardScreen },
  { key: "offers", label: "Funding Offers", Component: OffersScreen },
  { key: "documents", label: "Documents", Component: DocumentsScreen },
];

/**
 * AppShowcase — three phones side-by-side on desktop, single auto-cycling phone on mobile.
 * The single/mobile mode auto-cycles every 4s.
 */
export const AppShowcase = ({ className }) => {
  const [active, setActive] = useState(0);
  const reduce = useReducedMotion();

  useEffect(() => {
    if (reduce) return;
    const id = setInterval(() => {
      setActive((v) => (v + 1) % SCREENS.length);
    }, 4200);
    return () => clearInterval(id);
  }, [reduce]);

  return (
    <div className={cn("relative w-full", className)} data-testid="app-showcase">
      {/* Desktop: 3 phones */}
      <div className="hidden items-end justify-center gap-10 md:flex">
        {SCREENS.map((s, i) => {
          const Component = s.Component;
          const isCenter = i === 1;
          return (
            <motion.div
              key={s.key}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{
                duration: 0.7,
                delay: 0.08 * i,
                ease: [0.22, 1, 0.36, 1],
              }}
              className={cn(
                "relative",
                !isCenter && "md:translate-y-8 lg:translate-y-10",
              )}
            >
              <PhoneMockup
                width={isCenter ? 320 : 280}
                glow={isCenter}
                float={isCenter}
                data-testid={`showcase-phone-${s.key}`}
              >
                <Component />
              </PhoneMockup>
            </motion.div>
          );
        })}
      </div>

      {/* Mobile: single cycling phone */}
      <div className="flex flex-col items-center gap-6 md:hidden">
        <PhoneMockup width={280} data-testid="showcase-phone-mobile">
          <AnimatePresence mode="wait">
            <motion.div
              key={SCREENS[active].key}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.4 }}
              className="h-full w-full"
            >
              {React.createElement(SCREENS[active].Component)}
            </motion.div>
          </AnimatePresence>
        </PhoneMockup>

        {/* Dots */}
        <div className="flex items-center gap-2">
          {SCREENS.map((s, i) => (
            <button
              key={s.key}
              type="button"
              onClick={() => setActive(i)}
              aria-label={`Show ${s.label} screen`}
              className={cn(
                "h-1.5 rounded-full transition-all duration-300",
                active === i
                  ? "w-6 bg-brand-emerald"
                  : "w-1.5 bg-white/20 hover:bg-white/40",
              )}
              data-testid={`showcase-dot-${s.key}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default AppShowcase;
